import {
  Excalidraw,
  LiveCollaborationTrigger,
  TTDDialogTrigger,
  CaptureUpdateAction,
  reconcileElements,
  useEditorInterface,
} from "@excalidraw/excalidraw";
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import { getDefaultAppState } from "@excalidraw/excalidraw/appState";
import {
  CommandPalette,
  DEFAULT_CATEGORIES,
} from "@excalidraw/excalidraw/components/CommandPalette/CommandPalette";
import { ErrorDialog } from "@excalidraw/excalidraw/components/ErrorDialog";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { OverwriteConfirmDialog } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirm";
import { openConfirmModal } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirmState";
import { ShareableLinkDialog } from "@excalidraw/excalidraw/components/ShareableLinkDialog";
import Trans from "@excalidraw/excalidraw/components/Trans";
import {
  APP_NAME,
  EVENT,
  THEME,
  VERSION_TIMEOUT,
  debounce,
  getVersion,
  getFrame,
  isTestEnv,
  preventUnload,
  resolvablePromise,
  isRunningInIframe,
  isDevEnv,
} from "@excalidraw/common";
import polyfill from "@excalidraw/excalidraw/polyfill";
import { useCallback, useEffect, useRef, useState } from "react";
import { loadFromBlob } from "@excalidraw/excalidraw/data/blob";
import { TopBar as SpryTopBar, useSpryAccountManager } from "spry-apps-dropdown";
import { useCallbackRefState } from "@excalidraw/excalidraw/hooks/useCallbackRefState";
import { t } from "@excalidraw/excalidraw/i18n";

import {
  GithubIcon,
  XBrandIcon,
  DiscordIcon,
  ExcalLogo,
  usersIcon,
  share,
  youtubeIcon,
} from "@excalidraw/excalidraw/components/icons";
import { isElementLink } from "@excalidraw/element";
import { restore, restoreAppState } from "@excalidraw/excalidraw/data/restore";
import { newElementWith } from "@excalidraw/element";
import { isInitializedImageElement } from "@excalidraw/element";
import clsx from "clsx";
import {
  parseLibraryTokensFromUrl,
  useHandleLibrary,
} from "@excalidraw/excalidraw/data/library";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type { RestoredDataState } from "@excalidraw/excalidraw/data/restore";
import type {
  FileId,
  NonDeletedExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  ExcalidrawImperativeAPI,
  BinaryFiles,
  ExcalidrawInitialDataState,
  UIAppState,
} from "@excalidraw/excalidraw/types";
import type { ResolutionType } from "@excalidraw/common/utility-types";
import type { ResolvablePromise } from "@excalidraw/common/utils";

import CustomStats from "./CustomStats";
import {
  Provider,
  useAtom,
  useAtomValue,
  useAtomWithInitialValue,
  appJotaiStore,
} from "./app-jotai";
import {
  FIREBASE_STORAGE_PREFIXES,
  isExcalidrawPlusSignedUser,
  STORAGE_KEYS,
  SYNC_BROWSER_TABS_TIMEOUT,
} from "./app_constants";
import Collab, {
  collabAPIAtom,
  isCollaboratingAtom,
  isOfflineAtom,
} from "./collab/Collab";
import { AppFooter } from "./components/AppFooter";
import { AppMainMenu } from "./components/AppMainMenu";
import { AppWelcomeScreen } from "./components/AppWelcomeScreen";

import { TopErrorBoundary } from "./components/TopErrorBoundary";

import {
  exportToBackend,
  getCollaborationLinkData,
  isCollaborationLink,
  loadScene,
} from "./data";

import { updateStaleImageStatuses } from "./data/FileManager";
import {
  importFromLocalStorage,
  importUsernameFromLocalStorage,
} from "./data/localStorage";

import { loadFilesFromFirebase } from "./data/firebase";
import { loadFilesFromBackend } from "./data/RestBackend";
import {
  LibraryIndexedDBAdapter,
  LibraryLocalStorageMigrationAdapter,
  LocalData,
  localStorageQuotaExceededAtom,
} from "./data/LocalData";
import { isBrowserStorageStateNewer } from "./data/tabSync";
import { ShareDialog, shareDialogStateAtom } from "./share/ShareDialog";
import CollabError, { collabErrorIndicatorAtom } from "./collab/CollabError";
import { useHandleAppTheme } from "./useHandleAppTheme";
import { getPreferredLanguage } from "./app-language/language-detector";
import { useAppLangCode } from "./app-language/language-state";
import DebugCanvas, {
  debugRenderer,
  isVisualDebuggerEnabled,
  loadSavedDebugState,
} from "./components/DebugCanvas";
import { AIComponents } from "./components/AI";
import {
  PromptToTextTrigger,
  PromptToImageTrigger,
} from "./components/AIToolbarTriggers";


import "./index.scss";


import { AppSidebar } from "./components/AppSidebar";
import { SpryDrivePicker } from "./components/SpryDrivePicker";
import {
  setActiveBoardContext,
  getActiveBoardContext,
  triggerAutoSave,
  setBoardBootstrapState,
  resetBoardAutoCreateState,
} from "./data";
import {
  boardAPI,
  setAccessToken,
  spryboardAPI,
  extractBoardFromResponse,
  extractSpryboardLoadFromResponse,
} from "./api/backend";
import { useAuth } from "./hooks/useAuth";
import { isSdkMode } from "./sdk/sdkAuthBridge";

import type { CollabAPI } from "./collab/Collab";

polyfill();

window.EXCALIDRAW_THROTTLE_RENDER = true;

declare global {
  interface BeforeInstallPromptEventChoiceResult {
    outcome: "accepted" | "dismissed";
  }

  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<BeforeInstallPromptEventChoiceResult>;
  }

  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

let pwaEvent: BeforeInstallPromptEvent | null = null;

// Adding a listener outside of the component as it may (?) need to be
// subscribed early to catch the event.
//
// Also note that it will fire only if certain heuristics are met (user has
// used the app for some time, etc.)
window.addEventListener(
  "beforeinstallprompt",
  (event: BeforeInstallPromptEvent) => {
    // prevent Chrome <= 67 from automatically showing the prompt
    event.preventDefault();
    // cache for later use
    pwaEvent = event;
  },
);

let isSelfEmbedding = false;

if (window.self !== window.top) {
  try {
    const parentUrl = new URL(document.referrer);
    const currentUrl = new URL(window.location.href);
    if (parentUrl.origin === currentUrl.origin) {
      isSelfEmbedding = true;
    }
  } catch (error) {
    // ignore
  }
}

const shareableLinkConfirmDialog = {
  title: t("overwriteConfirm.modal.shareableLink.title"),
  description: (
    <Trans
      i18nKey="overwriteConfirm.modal.shareableLink.description"
      bold={(text) => <strong>{text}</strong>}
      br={() => <br />}
    />
  ),
  actionLabel: t("overwriteConfirm.modal.shareableLink.button"),
  color: "danger",
} as const;

const normalizeAppStateForRestore = (appState: any) => {
  const normalized = { ...(appState || {}) } as Record<string, any>;
  const collaborators = normalized.collaborators;
  if (collaborators instanceof Map) {
    normalized.collaborators = collaborators;
  } else if (Array.isArray(collaborators)) {
    normalized.collaborators = new Map(collaborators);
  } else {
    normalized.collaborators = new Map();
  }
  return normalized;
};

const createStableRoomKey = (roomId: string) => {
  const normalized = roomId.replace(/[^a-zA-Z0-9]/g, "");
  const seed = normalized || "spryboardroom";
  return `${seed}${seed}spryworkspace`.slice(0, 22).padEnd(22, "0");
};

const createAutoRoomLinkData = (roomId: string) => ({
  roomId,
  roomKey: createStableRoomKey(roomId),
});

const initializeScene = async (opts: {
  collabAPI: CollabAPI | null;
  excalidrawAPI: ExcalidrawImperativeAPI;
}): Promise<
  { scene: ExcalidrawInitialDataState | null } & (
    | { isExternalScene: true; id: string; key: string }
    | { isExternalScene: false; id?: null; key?: null }
  )
> => {
  const searchParams = new URLSearchParams(window.location.search);
  const id = searchParams.get("id");
  const boardId = searchParams.get("boardId");
  const fileId = searchParams.get("fileId");
  const jsonBackendMatch = window.location.hash.match(
    /^#json=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/,
  );
  const externalUrlMatch = window.location.hash.match(/^#url=(.*)$/);

  const localDataState = importFromLocalStorage();

  // Note: Session/local storage is the default. SpryDrive save is an explicit action.

  let scene: RestoredDataState & {
    scrollToContent?: boolean;
  } = await loadScene(null, null, localDataState);

  let roomLinkData = await getCollaborationLinkData(window.location.href);

  const isExternalScene = !!(id || jsonBackendMatch || roomLinkData || boardId || fileId);
  const isBoardOrDriveScene = !!(boardId || fileId);

  // SpryWorkspace: Load board from backend if boardId or fileId is in URL
  if (boardId || fileId) {
    try {
      let boardData: any = null;

      if (boardId) {
        console.log('[SpryBoard] Loading board from backend:', boardId);
        const response = await boardAPI.getBoard(boardId, { useDrive: true });
        const payload = extractBoardFromResponse(response);
        if (payload) {
          boardData = {
            id: payload.id,
            driveFileId: payload.driveFileId || null,
            title: payload.title || "Untitled Board",
            data: payload.data || null,
            canEdit: payload.canEdit !== false,
            accessRole: payload.accessRole || null,
          };
        }
      } else if (fileId) {
        console.log('[SpryBoard] Loading board from SpryDrive file:', fileId);
        const response = await spryboardAPI.load(fileId);
        const payload = extractSpryboardLoadFromResponse(response);
        // Strip .spryboard extension from filename for display
        const rawFilename = payload.filename || "Untitled Board";
        const displayTitle = rawFilename.replace(/\.spryboard$/i, '').trim() || "Untitled Board";
        boardData = {
          id: payload.boardId || null,
          driveFileId: payload.fileId || fileId,
          title: displayTitle,
          data: payload.boardData || payload.data || null,
          canEdit: payload.canEdit !== false,
          accessRole: payload.accessRole || null,
        };
      }

      if (boardData) {
        const initialData = boardData.data || {
          elements: [],
          appState: {},
          files: {},
        };
        const resolvedBoardId = boardData.id || boardId || null;
        const resolvedFileId = boardData.driveFileId || fileId || null;

        // Set the active board context for auto-save and collaboration
        setActiveBoardContext({
          boardId: resolvedBoardId,
          driveFileId: resolvedFileId,
          title: boardData.title || "Untitled Board",
          initialData,
          canEdit: boardData.canEdit ?? true,
          accessRole: boardData.accessRole ?? null,
        });

        const url = new URL(window.location.href);
        let shouldReplaceUrl = false;
        if (
          resolvedBoardId &&
          url.searchParams.get("boardId") !== resolvedBoardId
        ) {
          url.searchParams.set("boardId", resolvedBoardId);
          shouldReplaceUrl = true;
        }
        if (resolvedFileId) {
          if (url.searchParams.get("fileId") !== resolvedFileId) {
            url.searchParams.set("fileId", resolvedFileId);
            shouldReplaceUrl = true;
          }
        } else if (url.searchParams.has("fileId")) {
          url.searchParams.delete("fileId");
          shouldReplaceUrl = true;
        }
        if (shouldReplaceUrl) {
          window.history.replaceState(
            {},
            APP_NAME,
            `${url.pathname}${url.search}${url.hash}`,
          );
        }

        const parsedData = typeof initialData === "string"
          ? JSON.parse(initialData)
          : initialData;

        const restoredData = restore(
          {
            elements: parsedData?.elements || [],
            appState: normalizeAppStateForRestore(parsedData?.appState || {}),
            files: parsedData?.files || {},
          },
          localDataState?.appState,
          null,
          { repairBindings: true },
        );

        scene = {
          elements: restoredData.elements,
          appState: restoredData.appState,
          files: restoredData.files,
          scrollToContent: true,
        };
      }
    } catch (error) {
      console.error('[SpryBoard] Failed to load board from backend:', error);
    }
  }

  if (isExternalScene && !isBoardOrDriveScene) {
    if (
      // don't prompt if scene is empty
      !scene.elements.length ||
      // don't prompt for collab scenes because we don't override local storage
      roomLinkData ||
      // otherwise, prompt whether user wants to override current scene
      (await openConfirmModal(shareableLinkConfirmDialog))
    ) {
      if (jsonBackendMatch) {
        scene = await loadScene(
          jsonBackendMatch[1],
          jsonBackendMatch[2],
          localDataState,
        );
      }
      scene.scrollToContent = true;
      if (!roomLinkData && !isBoardOrDriveScene) {
        window.history.replaceState({}, APP_NAME, window.location.origin);
      }
    } else {
      // https://github.com/excalidraw/excalidraw/issues/1919
      if (document.hidden) {
        return new Promise((resolve, reject) => {
          window.addEventListener(
            "focus",
            () => initializeScene(opts).then(resolve).catch(reject),
            {
              once: true,
            },
          );
        });
      }

      roomLinkData = null;
      if (!isBoardOrDriveScene) {
        window.history.replaceState({}, APP_NAME, window.location.origin);
      }
    }
  } else if (externalUrlMatch) {
    window.history.replaceState({}, APP_NAME, window.location.origin);

    const url = externalUrlMatch[1];
    try {
      const request = await fetch(window.decodeURIComponent(url));
      const data = await loadFromBlob(await request.blob(), null, null);
      if (
        !scene.elements.length ||
        (await openConfirmModal(shareableLinkConfirmDialog))
      ) {
        return { scene: data, isExternalScene };
      }
    } catch (error: any) {
      return {
        scene: {
          appState: {
            errorMessage: t("alerts.invalidSceneUrl"),
          },
        },
        isExternalScene,
      };
    }
  }

  if (!roomLinkData && isBoardOrDriveScene && opts.collabAPI) {
    const context = getActiveBoardContext();
    const autoRoomId =
      context.boardId || context.driveFileId || boardId || fileId;
    if (autoRoomId) {
      roomLinkData = createAutoRoomLinkData(autoRoomId);
    }
  }

  if (roomLinkData && opts.collabAPI) {
    const { excalidrawAPI } = opts;

    // =========================================================================
    // SPRYWORKSPACE: Start collaboration with board context
    // =========================================================================
    // For boardId/fileId routes we already loaded the scene from the API; pass
    // preloadedScene so collab doesn't reset/re-fetch and clobber the loaded data.
    const collabScene = await opts.collabAPI.startCollaboration(
      roomLinkData,
      isBoardOrDriveScene ? { preloadedScene: true } : undefined,
    );

    // If we pre-loaded the scene OR the collab room is empty, return the scene
    // we already loaded from the backend earlier in this function. The collab
    // path returns a partial scene (no files, no full appState) so always
    // prefer the locally-loaded scene when we have one.
    if (
      isBoardOrDriveScene ||
      ((!collabScene?.elements || collabScene.elements.length === 0) &&
        scene.elements.length > 0)
    ) {
      return {
        scene: {
          elements: scene.elements,
          appState: {
            ...restoreAppState(
              {
                ...scene.appState,
                theme: localDataState?.appState?.theme || scene.appState?.theme,
              },
              excalidrawAPI.getAppState(),
            ),
            isLoading: false,
          },
          files: scene.files,
          scrollToContent: scene.scrollToContent,
        },
        isExternalScene: true,
        id: roomLinkData.roomId,
        key: roomLinkData.roomKey,
      };
    }

    return {
      // when collaborating, the state may have already been updated at this
      // point (we may have received updates from other clients), so reconcile
      // elements and appState with existing state
      scene: {
        ...collabScene,
        appState: {
          ...restoreAppState(
            {
              ...collabScene?.appState,
              theme: localDataState?.appState?.theme || collabScene?.appState?.theme,
            },
            excalidrawAPI.getAppState(),
          ),
          // necessary if we're invoking from a hashchange handler which doesn't
          // go through App.initializeScene() that resets this flag
          isLoading: false,
        },
        elements: reconcileElements(
          collabScene?.elements || [],
          excalidrawAPI.getSceneElementsIncludingDeleted() as RemoteExcalidrawElement[],
          excalidrawAPI.getAppState(),
        ),
      },
      isExternalScene: true,
      id: roomLinkData.roomId,
      key: roomLinkData.roomKey,
    };
  } else if (scene && isBoardOrDriveScene) {
    const context = getActiveBoardContext();
    const resolvedId = context.boardId || context.driveFileId || boardId || fileId;
    if (resolvedId) {
      return {
        scene,
        isExternalScene: true,
        id: resolvedId,
        key: null,
      };
    }
  } else if (scene) {
    return isExternalScene && jsonBackendMatch
      ? {
        scene,
        isExternalScene,
        id: jsonBackendMatch[1],
        key: jsonBackendMatch[2],
      }
      : { scene, isExternalScene: false };
  }




  return { scene: null, isExternalScene: false };
};

const ExcalidrawWrapper = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const isCollabDisabled = isRunningInIframe();
  const {
    user,
    token: authToken,
    loading: authLoading,
    login,
    logout,
    error: authError,
  } = useAuth();
  const sdkEmbedded = isSdkMode();
  // Account manager pulls from OIDC context which doesn't exist in SDK mode.
  // Skip it (and the TopBar that consumes it) when embedded as an SDK.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const accountManager = sdkEmbedded ? null : useSpryAccountManager();
  const loginAttemptedRef = useRef(false);

  const { editorTheme, appTheme, setAppTheme } = useHandleAppTheme();

  const [langCode, setLangCode] = useAppLangCode();

  const editorInterface = useEditorInterface();

  // SpryDrive picker state
  const [showDrivePicker, setShowDrivePicker] = useState(false);


  // initial state
  // ---------------------------------------------------------------------------

  const initialStatePromiseRef = useRef<{
    promise: ResolvablePromise<ExcalidrawInitialDataState | null>;
  }>({ promise: null! });
  if (!initialStatePromiseRef.current.promise) {
    initialStatePromiseRef.current.promise =
      resolvablePromise<ExcalidrawInitialDataState | null>();
  }
  const [fileInfo, setFileInfo] = useState<{
    boardId: string;
    title: string;
  } | null>(null);

  const [boardTitle, setBoardTitle] = useState<string>("Untitled Board");
  const [titleEditing, setTitleEditing] = useState(false);
  const [renameDraft, setRenameDraft] = useState("Untitled Board");
  const [isRenameBoardDialogOpen, setIsRenameBoardDialogOpen] = useState(false);
  const [boardAccess, setBoardAccess] = useState<{
    canEdit: boolean;
    accessRole: string | null;
  }>({ canEdit: true, accessRole: null });

  const debugCanvasRef = useRef<HTMLCanvasElement>(null);
  const isBoardReadOnly = boardAccess.canEdit === false;
  // Ref so stable useEffect closures (syncData, etc.) can read the current
  // value without the effect needing isBoardReadOnly in its deps array.
  const isBoardReadOnlyRef = useRef(false);
  isBoardReadOnlyRef.current = isBoardReadOnly;
  const activeBoardContext = getActiveBoardContext();
  const hasLinkedBoardContext = !!(activeBoardContext.boardId || activeBoardContext.driveFileId);
  const canShareBoardActions = !hasLinkedBoardContext || boardAccess.accessRole === "owner";

  useEffect(() => {
    trackEvent("load", "frame", getFrame());
    // Delayed so that the app has a time to load the latest SW
    setTimeout(() => {
      trackEvent("load", "version", getVersion());
    }, VERSION_TIMEOUT);
  }, []);

  const [excalidrawAPI, excalidrawRefCallback] =
    useCallbackRefState<ExcalidrawImperativeAPI>();

  const [, setShareDialogState] = useAtom(shareDialogStateAtom);
  const [collabAPI] = useAtom(collabAPIAtom);
  const [isCollaborating] = useAtomWithInitialValue(isCollaboratingAtom, () => {
    return isCollaborationLink(window.location.href);
  });
  const collabError = useAtomValue(collabErrorIndicatorAtom);

  useEffect(() => {
    setAccessToken(authToken);
  }, [authToken]);

  useEffect(() => {
    if (sdkEmbedded) {
      // Host owns auth; never trigger OIDC redirect from SDK mode.
      return;
    }
    if (authLoading || authToken || loginAttemptedRef.current) {
      return;
    }
    if (window.location.pathname === "/auth/oidc/callback") {
      return;
    }
    loginAttemptedRef.current = true;
    void login();
  }, [authLoading, authToken, login]);

  useEffect(() => {
    if (!collabAPI || !user) {
      return;
    }
    const username = user.name || user.email;
    if (username) {
      collabAPI.setUsername(username);
    }
  }, [collabAPI, user]);

  useHandleLibrary({
    excalidrawAPI,
    adapter: LibraryIndexedDBAdapter,
    // TODO maybe remove this in several months (shipped: 24-03-11)
    migrationAdapter: LibraryLocalStorageMigrationAdapter,
  });

  const [, forceRefresh] = useState(false);

  const navigateToBoardTarget = useCallback((target: {
    boardId?: string | null;
    fileId?: string | null;
  }) => {
    const url = new URL(window.location.href);
    if (target.boardId) {
      url.searchParams.set("boardId", target.boardId);
    } else {
      url.searchParams.delete("boardId");
    }
    if (target.fileId) {
      url.searchParams.set("fileId", target.fileId);
    } else {
      url.searchParams.delete("fileId");
    }
    window.location.href = url.toString();
  }, []);

  const createBoardAndNavigate = useCallback(async () => {
    try {
      const response = await boardAPI.createBoard({
        title: "Untitled Board",
        data: { elements: [], appState: {}, files: {} },
      });

      const newBoard = extractBoardFromResponse(response);

      if (newBoard?.id) {
        navigateToBoardTarget({
          boardId: newBoard.id,
          fileId: newBoard.driveFileId || null,
        });
      }
    } catch (error) {
      console.error("Failed to create new board:", error);
    }
  }, [navigateToBoardTarget]);

  const openBoardTarget = useCallback((target: {
    boardId?: string;
    fileId?: string;
  }) => {
    navigateToBoardTarget({
      boardId: target.boardId || null,
      fileId: target.fileId || null,
    });
  }, [navigateToBoardTarget]);

  useEffect(() => {
    if (isDevEnv()) {
      const debugState = loadSavedDebugState();

      if (debugState.enabled && !window.visualDebug) {
        window.visualDebug = {
          data: [],
        };
      } else {
        delete window.visualDebug;
      }
      forceRefresh((prev) => !prev);
    }
  }, [excalidrawAPI]);

  const syncBoardAccessFromContext = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const hasBoardRoute = !!(params.get("boardId") || params.get("fileId"));
    if (!hasBoardRoute) {
      setBoardAccess({ canEdit: true, accessRole: null });
      return;
    }

    const ctx = getActiveBoardContext();
    setBoardAccess({
      canEdit: ctx.canEdit !== false,
      accessRole: typeof ctx.accessRole === "string" ? ctx.accessRole : null,
    });
  }, []);

  useEffect(() => {
    if (!authToken || !excalidrawAPI || (!isCollabDisabled && !collabAPI)) {
      return;
    }

    const loadImages = (
      data: ResolutionType<typeof initializeScene>,
      isInitialLoad = false,
    ) => {
      if (!data.scene) {
        return;
      }
      if (collabAPI?.isCollaborating()) {
        if (data.scene.elements) {
          collabAPI
            .fetchImageFilesFromBackend({
              elements: data.scene.elements,
              forceFetchFiles: true,
            })
            .then(({ loadedFiles, erroredFiles }) => {
              excalidrawAPI.addFiles(loadedFiles);
              updateStaleImageStatuses({
                excalidrawAPI,
                erroredFiles,
                elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
              });
            });
        }
      } else {
        const fileIds =
          data.scene.elements?.reduce((acc, element) => {
            if (isInitializedImageElement(element)) {
              return acc.concat(element.fileId);
            }
            return acc;
          }, [] as FileId[]) || [];

        if (data.isExternalScene) {
          loadFilesFromFirebase(
            `${FIREBASE_STORAGE_PREFIXES.shareLinkFiles}/${data.id}`,
            data.key,
            fileIds,
          ).then(({ loadedFiles, erroredFiles }) => {
            excalidrawAPI.addFiles(loadedFiles);
            updateStaleImageStatuses({
              excalidrawAPI,
              erroredFiles,
              elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
            });
          });
        } else if (isInitialLoad) {
          // Check if we have a boardId or fileId from SpryDrive
          const ctx = getActiveBoardContext();
          if (ctx.boardId || ctx.driveFileId) {
            // Load images from SpryBoard backend
            if (fileIds.length) {
              loadFilesFromBackend(ctx.boardId || ctx.driveFileId || '', '', fileIds)
                .then(({ loadedFiles, erroredFiles }) => {
                  if (loadedFiles.length) {
                    excalidrawAPI.addFiles(loadedFiles);
                  }
                  updateStaleImageStatuses({
                    excalidrawAPI,
                    erroredFiles,
                    elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                  });
                })
                .catch((error) => {
                  console.warn('[SpryBoard] Failed to load images from backend:', error);
                  // Fallback to IndexedDB
                  LocalData.fileStorage
                    .getFiles(fileIds)
                    .then(({ loadedFiles, erroredFiles }) => {
                      if (loadedFiles.length) {
                        excalidrawAPI.addFiles(loadedFiles);
                      }
                      updateStaleImageStatuses({
                        excalidrawAPI,
                        erroredFiles,
                        elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                      });
                    });
                });
            }
          } else if (fileIds.length) {
            // No SpryDrive context, use IndexedDB
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
          // on fresh load, clear unused files from IDB (from previous
          // session)
          LocalData.fileStorage.clearObsoleteFiles({ currentFileIds: fileIds });
        }
      }
    };

    const loadInitialScene = () => {
      resetBoardAutoCreateState();
      setBoardBootstrapState({
        isInitialRouteResolving: true,
        hasInitialRouteResolved: false,
      });
      return initializeScene({ collabAPI, excalidrawAPI })
        .then(async (data) => {
          loadImages(data, /* isInitialLoad */ true);
          initialStatePromiseRef.current.promise.resolve(data.scene);
          const resolvedTitle = getActiveBoardContext().title || "Untitled Board";
          setBoardTitle(resolvedTitle);
          setRenameDraft(resolvedTitle);
          syncBoardAccessFromContext();
        })
        .finally(() => {
          setBoardBootstrapState({
            isInitialRouteResolving: false,
            hasInitialRouteResolved: true,
          });
        });
    };

    void loadInitialScene();

    const onHashChange = async (event: HashChangeEvent) => {
      event.preventDefault();
      const libraryUrlTokens = parseLibraryTokensFromUrl();
      if (!libraryUrlTokens) {
        if (
          collabAPI?.isCollaborating() &&
          !isCollaborationLink(window.location.href)
        ) {
          collabAPI.stopCollaboration(false);
        }
        excalidrawAPI.updateScene({ appState: { isLoading: true } });

        resetBoardAutoCreateState();
        setBoardBootstrapState({
          isInitialRouteResolving: true,
          hasInitialRouteResolved: false,
        });
        initializeScene({ collabAPI, excalidrawAPI })
          .then((data) => {
            loadImages(data);
            if (data.scene) {
              excalidrawAPI.updateScene({
                ...data.scene,
                ...restore(data.scene, null, null, { repairBindings: true }),
                captureUpdate: CaptureUpdateAction.IMMEDIATELY,
              });
            }
            syncBoardAccessFromContext();
          })
          .finally(() => {
            setBoardBootstrapState({
              isInitialRouteResolving: false,
              hasInitialRouteResolved: true,
            });
          });
      }
    };

    const syncData = debounce(() => {
      if (isTestEnv()) {
        return;
      }
      if (
        !document.hidden &&
        ((collabAPI && !collabAPI.isCollaborating()) || isCollabDisabled)
      ) {
        // don't sync if local state is newer or identical to browser state.
        // Also skip for read-only viewers — their canvas is authoritative from
        // the API load, not from localStorage.  isBoardReadOnlyRef is used here
        // because syncData lives in a stable closure that would capture a stale
        // isBoardReadOnly value if we used the variable directly.
        if (!isBoardReadOnlyRef.current && isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_DATA_STATE)) {
          const localDataState = importFromLocalStorage();
          const username = user?.name || user?.email || importUsernameFromLocalStorage();
          setLangCode(getPreferredLanguage());
          excalidrawAPI.updateScene({
            ...localDataState,
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          LibraryIndexedDBAdapter.load().then((data) => {
            if (data) {
              excalidrawAPI.updateLibrary({
                libraryItems: data.libraryItems,
              });
            }
          });
          collabAPI?.setUsername(username || "");
        }

        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_FILES)) {
          const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
          const currFiles = excalidrawAPI.getFiles();
          const fileIds =
            elements?.reduce((acc, element) => {
              if (
                isInitializedImageElement(element) &&
                // only load and update images that aren't already loaded
                !currFiles[element.fileId]
              ) {
                return acc.concat(element.fileId);
              }
              return acc;
            }, [] as FileId[]) || [];
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
        }
      }
    }, SYNC_BROWSER_TABS_TIMEOUT);

    const onUnload = () => {
      LocalData.flushSave();
    };

    const visibilityChange = (event: FocusEvent | Event) => {
      if (event.type === EVENT.BLUR || document.hidden) {
        LocalData.flushSave();
      }
      if (
        event.type === EVENT.VISIBILITY_CHANGE ||
        event.type === EVENT.FOCUS
      ) {
        syncData();
      }
    };

    window.addEventListener(EVENT.HASHCHANGE, onHashChange, false);
    window.addEventListener(EVENT.UNLOAD, onUnload, false);
    window.addEventListener(EVENT.BLUR, visibilityChange, false);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, visibilityChange, false);
    window.addEventListener(EVENT.FOCUS, visibilityChange, false);
    return () => {
      window.removeEventListener(EVENT.HASHCHANGE, onHashChange, false);
      window.removeEventListener(EVENT.UNLOAD, onUnload, false);
      window.removeEventListener(EVENT.BLUR, visibilityChange, false);
      window.removeEventListener(EVENT.FOCUS, visibilityChange, false);
      document.removeEventListener(
        EVENT.VISIBILITY_CHANGE,
        visibilityChange,
        false,
      );
    };
  }, [authToken, isCollabDisabled, collabAPI, excalidrawAPI, setLangCode, user, syncBoardAccessFromContext]);

  useEffect(() => {
    const unloadHandler = (event: BeforeUnloadEvent) => {
      LocalData.flushSave();

      if (
        excalidrawAPI &&
        LocalData.fileStorage.shouldPreventUnload(
          excalidrawAPI.getSceneElements(),
        )
      ) {
        if (import.meta.env.VITE_APP_DISABLE_PREVENT_UNLOAD !== "true") {
          preventUnload(event);
        } else {
          console.warn(
            "preventing unload disabled (VITE_APP_DISABLE_PREVENT_UNLOAD)",
          );
        }
      }
    };
    window.addEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    return () => {
      window.removeEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    };
  }, [excalidrawAPI]);

  // Listen for SpryDrive picker open event
  useEffect(() => {
    const handleOpenPicker = () => {
      setShowDrivePicker(true);
    };
    window.addEventListener("spryboard:open-picker", handleOpenPicker);
    return () => {
      window.removeEventListener("spryboard:open-picker", handleOpenPicker);
    };
  }, []);

  // Store excalidrawAPI in window for menu access
  useEffect(() => {
    if (excalidrawAPI) {
      (window as any).excalidrawAPI = excalidrawAPI;
    }
  }, [excalidrawAPI]);

  const onChange = (
    elements: readonly OrderedExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    if (!isBoardReadOnly && collabAPI?.isCollaborating()) {
      collabAPI.syncElements(elements);
    }

    // this check is redundant, but since this is a hot path, it's best
    // not to evaludate the nested expression every time.
    // Viewers must never write to localStorage: their board comes from the API,
    // and writing empty/stale elements here (e.g. on the first render before
    // the API load completes) would let syncData restore blank content the next
    // time the tab regains focus.
    if (!isBoardReadOnly && !LocalData.isSavePaused()) {
      LocalData.save(elements, appState, files, () => {
        if (excalidrawAPI) {
          let didChange = false;

          const elements = excalidrawAPI
            .getSceneElementsIncludingDeleted()
            .map((element) => {
              if (
                LocalData.fileStorage.shouldUpdateImageElementStatus(element)
              ) {
                const newElement = newElementWith(element, { status: "saved" });
                if (newElement !== element) {
                  didChange = true;
                }
                return newElement;
              }
              return element;
            });

          if (didChange) {
            excalidrawAPI.updateScene({
              elements,
              captureUpdate: CaptureUpdateAction.NEVER,
            });
          }
        }
      });
    }

    // SpryWorkspace: auto-save to SpryDrive (debounced)
    if (!isBoardReadOnly && excalidrawAPI && getActiveBoardContext()) {
      triggerAutoSave(elements, appState, files);
    }

    // Render the debug scene if the debug canvas is available
    if (debugCanvasRef.current && excalidrawAPI) {
      debugRenderer(
        debugCanvasRef.current,
        appState,
        elements,
        window.devicePixelRatio,
      );
    }

  };

  const [latestShareableLink, setLatestShareableLink] = useState<string | null>(
    null,
  );

  const persistBoardTitle = useCallback(async (rawTitle: string) => {
    if (isBoardReadOnly) {
      setErrorMessage("This board is view-only. Ask the owner to grant editor access.");
      return;
    }
    const newTitle = rawTitle.trim() || boardTitle || "Untitled Board";
    if (newTitle === boardTitle) {
      return;
    }

    const previousTitle = boardTitle || "Untitled Board";
    setBoardTitle(newTitle);
    setRenameDraft(newTitle);
    setActiveBoardContext({ title: newTitle });

    const ctx = getActiveBoardContext();
    try {
      if (ctx.boardId) {
        await boardAPI.updateBoard(ctx.boardId, { title: newTitle });
        return;
      }

      if (ctx.driveFileId) {
        const excalidrawAPI = (window as any).excalidrawAPI;
        const boardData = excalidrawAPI
          ? {
              elements: excalidrawAPI.getSceneElements(),
              appState: excalidrawAPI.getAppState(),
              files: excalidrawAPI.getFiles(),
            }
          : null;
        if (!boardData) {
          throw new Error("Editor not ready");
        }
        await spryboardAPI.autoSave(ctx.driveFileId, {
          boardData,
          filename: newTitle,
        });
      }
    } catch (err) {
      console.error("Failed to save board title:", err);
      setBoardTitle(previousTitle);
      setRenameDraft(previousTitle);
      setActiveBoardContext({ title: previousTitle });
      setErrorMessage("Failed to rename board");
    }
  }, [boardTitle, isBoardReadOnly]);

  const beginInlineRename = useCallback(() => {
    if (isBoardReadOnly) {
      setErrorMessage("This board is view-only. Ask the owner to grant editor access.");
      return;
    }
    const currentTitle = boardTitle || "Untitled Board";
    setRenameDraft(currentTitle);
    setIsRenameBoardDialogOpen(false);
    setTitleEditing(true);
  }, [boardTitle, isBoardReadOnly]);

  const cancelInlineRename = useCallback(() => {
    setTitleEditing(false);
    setRenameDraft(boardTitle || "Untitled Board");
  }, [boardTitle]);

  const handleTitleSave = useCallback(async () => {
    setTitleEditing(false);
    await persistBoardTitle(renameDraft);
  }, [persistBoardTitle, renameDraft]);

  const openRenameBoardDialog = useCallback(() => {
    if (isBoardReadOnly) {
      setErrorMessage("This board is view-only. Ask the owner to grant editor access.");
      return;
    }
    const currentTitle = boardTitle || "Untitled Board";
    setRenameDraft(currentTitle);
    setTitleEditing(false);
    setIsRenameBoardDialogOpen(true);
  }, [boardTitle, isBoardReadOnly]);

  const submitRenameBoardDialog = useCallback(async () => {
    await persistBoardTitle(renameDraft);
    setIsRenameBoardDialogOpen(false);
  }, [persistBoardTitle, renameDraft]);

  const onExportToBackend = async (
    exportedElements: readonly NonDeletedExcalidrawElement[],
    appState: Partial<AppState>,
    files: BinaryFiles,
  ) => {
    if (exportedElements.length === 0) {
      throw new Error(t("alerts.cannotExportEmptyCanvas"));
    }
    try {
      const { url, errorMessage } = await exportToBackend(
        exportedElements,
        {
          ...appState,
          viewBackgroundColor: appState.exportBackground
            ? appState.viewBackgroundColor
            : getDefaultAppState().viewBackgroundColor,
        },
        files,
      );

      if (errorMessage) {
        throw new Error(errorMessage);
      }

      if (url) {
        setLatestShareableLink(url);
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        const { width, height } = appState;
        console.error(error, {
          width,
          height,
          devicePixelRatio: window.devicePixelRatio,
        });
        throw new Error(error.message);
      }
    }
  };

  const renderCustomStats = (
    elements: readonly NonDeletedExcalidrawElement[],
    appState: UIAppState,
  ) => {
    return (
      <CustomStats
        setToast={(message) => excalidrawAPI!.setToast({ message })}
        appState={appState}
        elements={elements}
      />
    );
  };

  const isOffline = useAtomValue(isOfflineAtom);

  const localStorageQuotaExceeded = useAtomValue(localStorageQuotaExceededAtom);

  const onCollabDialogOpen = useCallback(
    () => {
      if (!canShareBoardActions) {
        setErrorMessage("Only the board owner can share this board.");
        return;
      }
      setShareDialogState({ isOpen: true, type: "collaborationOnly" });
    },
    [canShareBoardActions, setShareDialogState],
  );

  // browsers generally prevent infinite self-embedding, there are
  // cases where it still happens, and while we disallow self-embedding
  // by not whitelisting our own origin, this serves as an additional guard
  if (isSelfEmbedding) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        <h1>I'm not a pretzel!</h1>
      </div>
    );
  }

  if (authLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        <h1>Authenticating...</h1>
      </div>
    );
  }

  if (!authToken) {
    if (sdkEmbedded) {
      // Host hasn't supplied a token yet — render nothing rather than
      // an OIDC redirect prompt that doesn't apply in embedded mode.
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            height: "100%",
          }}
        >
          <h1>Loading…</h1>
        </div>
      );
    }
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.75rem",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
          padding: "1rem",
        }}
      >
        <h1>Sign in required</h1>
        <p>Redirecting to SpryLogin to continue.</p>
        {authError ? <p>{authError}</p> : null}
        <button onClick={() => void login()}>Continue with SpryLogin</button>
      </div>
    );
  }



  return (
    <div
      style={{ height: "100%" }}
      className={clsx("excalidraw-app", {
        "is-collaborating": isCollaborating,
      })}
    >
      <div className="spryboard-title-overlay">
        {titleEditing ? (
          <input
            className="spryboard-title-input"
            aria-label="Board title"
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            onBlur={() => void handleTitleSave()}
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleTitleSave();
              if (e.key === "Escape") {
                cancelInlineRename();
              }
            }}
            autoFocus
          />
        ) : (
          <button
            type="button"
            className={clsx("spryboard-title-btn", {
              "is-readonly": isBoardReadOnly,
            })}
            onClick={beginInlineRename}
            title={isBoardReadOnly ? "View-only board" : "Click to rename board"}
            disabled={isBoardReadOnly}
          >
            {isBoardReadOnly
              ? `Locked • ${boardTitle || "Untitled Board"}`
              : boardTitle || "Untitled Board"}
          </button>
        )}
      </div>
      {isBoardReadOnly && (
        <div className="spryboard-readonly-banner" role="status" aria-live="polite">
          🔒 View only access ({boardAccess.accessRole || "viewer"}) — editing is disabled
        </div>
      )}
      <Excalidraw
        excalidrawAPI={excalidrawRefCallback}
        onChange={onChange}
        initialData={initialStatePromiseRef.current.promise}
        viewModeEnabled={isBoardReadOnly}
        isCollaborating={isCollaborating}
        onPointerUpdate={collabAPI?.onPointerUpdate}
        UIOptions={{
          canvasActions: {
            toggleTheme: true,
            export: {
              onExportToBackend,
            },
          },
        }}
        langCode={langCode}
        renderCustomStats={renderCustomStats}
        detectScroll={false}
        handleKeyboardGlobally={true}
        autoFocus={true}
        theme={editorTheme}
        renderTopRightUI={(isMobile) => {
          if (isMobile) {
            return null;
          }

          return (
            <div className="excalidraw-ui-top-right spryboard-top-right">
              {!isCollabDisabled && collabAPI ? (
                <>
                  {collabError.message && <CollabError collabError={collabError} />}
                  {canShareBoardActions ? (
                    <LiveCollaborationTrigger
                      isCollaborating={isCollaborating}
                      onSelect={() =>
                        setShareDialogState({ isOpen: true, type: "share" })
                      }
                      editorInterface={editorInterface}
                    />
                  ) : null}
                </>
              ) : null}
              {!sdkEmbedded && accountManager ? (
                <div className="spry-apps-topbar-host spryboard-topbar-host">
                  <SpryTopBar
                    apiUrl={import.meta.env.VITE_APPS_API_URL || 'https://sprylogin.com/apps-api'}
                    onSignOut={logout}
                    getAuthToken={() => authToken}
                    accountManager={accountManager}
                    appsRefetchInterval={5 * 60 * 1000}
                    profileRefetchInterval={5 * 60 * 1000}
                  />
                </div>
              ) : null}
            </div>
          );
        }}
        onLinkOpen={(element, event) => {
          if (element.link && isElementLink(element.link)) {
            event.preventDefault();
            excalidrawAPI?.scrollToContent(element.link, { animate: true });
          }
        }}
      >
        <AppMainMenu
          onCollabDialogOpen={onCollabDialogOpen}
          onCreateBoard={() => {
            void createBoardAndNavigate();
          }}
          onOpenPicker={() => setShowDrivePicker(true)}
          onRenameBoard={openRenameBoardDialog}
          isCollaborating={isCollaborating}
          isCollabEnabled={!isCollabDisabled}
          isReadOnlyBoard={isBoardReadOnly}
          canShareBoard={canShareBoardActions}
          theme={appTheme}
          setTheme={(theme) => setAppTheme(theme)}
          refresh={() => forceRefresh((prev) => !prev)}
        />
        <AppWelcomeScreen
          onCollabDialogOpen={onCollabDialogOpen}
          onCreateBoard={() => {
            void createBoardAndNavigate();
          }}
          onOpenFromSpryDrive={() => setShowDrivePicker(true)}
          showStartChoices={
            !getActiveBoardContext().boardId && !getActiveBoardContext().driveFileId
          }
          isCollabEnabled={!isCollabDisabled}
        />
        <OverwriteConfirmDialog>
          <OverwriteConfirmDialog.Actions.ExportToImage />
          <OverwriteConfirmDialog.Actions.SaveToDisk />
        </OverwriteConfirmDialog>
        <AppFooter onChange={() => excalidrawAPI?.refresh()} />
        {excalidrawAPI && <AIComponents excalidrawAPI={excalidrawAPI} />}

        <TTDDialogTrigger />
        <PromptToTextTrigger />
        <PromptToImageTrigger />
        
        {isCollaborating && isOffline && (
          <div className="alertalert--warning">
            {t("alerts.collabOfflineWarning")}
          </div>
        )}
        {localStorageQuotaExceeded && (
          <div className="alert alert--danger">
            {t("alerts.localStorageQuotaExceeded")}
          </div>
        )}
        {latestShareableLink && (
          <ShareableLinkDialog
            link={latestShareableLink}
            onCloseRequest={() => setLatestShareableLink(null)}
            setErrorMessage={setErrorMessage}
          />
        )}
        {isRenameBoardDialogOpen && (
          <Dialog
            size="small"
            title="Rename Board"
            onCloseRequest={() => setIsRenameBoardDialogOpen(false)}
          >
            <div style={{ display: "grid", gap: 12 }}>
              <label
                htmlFor="spryboard-rename-input"
                style={{ fontSize: 14, fontWeight: 600 }}
              >
                Board name
              </label>
              <input
                id="spryboard-rename-input"
                value={renameDraft}
                onChange={(event) => setRenameDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    void submitRenameBoardDialog();
                  }
                  if (event.key === "Escape") {
                    setIsRenameBoardDialogOpen(false);
                  }
                }}
                placeholder="Untitled Board"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid var(--color-border, #d0d7de)",
                  fontSize: 14,
                }}
              />
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setIsRenameBoardDialogOpen(false)}
                  style={{ padding: "8px 12px" }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void submitRenameBoardDialog()}
                  style={{ padding: "8px 12px", fontWeight: 600 }}
                >
                  Save
                </button>
              </div>
            </div>
          </Dialog>
        )}
        {excalidrawAPI && !isCollabDisabled && (
          <Collab
            excalidrawAPI={excalidrawAPI}
            currentUserId={user?.id ?? null}
            currentUserAvatar={user?.avatar}
          />
        )}

        <ShareDialog
          collabAPI={collabAPI}
          excalidrawAPI={excalidrawAPI ?? null}
          onExportToBackend={async () => {
            if (excalidrawAPI) {
              try {
                await onExportToBackend(
                  excalidrawAPI.getSceneElements(),
                  excalidrawAPI.getAppState(),
                  excalidrawAPI.getFiles(),
                );
              } catch (error: any) {
                setErrorMessage(error.message);
              }
            }
          }}
        />

        <AppSidebar />

        {errorMessage && (
          <ErrorDialog onClose={() => setErrorMessage("")}>
            {errorMessage}
          </ErrorDialog>
        )}

        <CommandPalette
          customCommandPaletteItems={[
            {
              label: t("labels.liveCollaboration"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => canShareBoardActions,
              keywords: [
                "team",
                "multiplayer",
                "share",
                "public",
                "session",
                "invite",
              ],
              icon: usersIcon,
              perform: () => {
                if (!canShareBoardActions) {
                  setErrorMessage("Only the board owner can share this board.");
                  return;
                }
                setShareDialogState({
                  isOpen: true,
                  type: "collaborationOnly",
                });
              },
            },
            {
              label: t("roomDialog.button_stopSession"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!collabAPI?.isCollaborating(),
              keywords: [
                "stop",
                "session",
                "end",
                "leave",
                "close",
                "exit",
                "collaboration",
              ],
              perform: () => {
                if (collabAPI) {
                  collabAPI.stopCollaboration();
                  if (!collabAPI.isCollaborating()) {
                    setShareDialogState({ isOpen: false });
                  }
                }
              },
            },
            {
              label: t("labels.share"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => canShareBoardActions,
              icon: share,
              keywords: [
                "link",
                "shareable",
                "readonly",
                "export",
                "publish",
                "snapshot",
                "url",
                "collaborate",
                "invite",
              ],
              perform: async () => {
                if (!canShareBoardActions) {
                  setErrorMessage("Only the board owner can share this board.");
                  return;
                }
                setShareDialogState({ isOpen: true, type: "share" });
              },
            },
            {
              ...CommandPalette.defaultItems.toggleTheme,
              perform: () => {
                setAppTheme(
                  editorTheme === THEME.DARK ? THEME.LIGHT : THEME.DARK,
                );
              },
            },
            {
              label: t("labels.installPWA"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!pwaEvent,
              perform: () => {
                if (pwaEvent) {
                  pwaEvent.prompt();
                  pwaEvent.userChoice.then(() => {
                    // event cannot be reused, but we'll hopefully
                    // grab new one as the event should be fired again
                    pwaEvent = null;
                  });
                }
              },
            },
          ]}
        />
        {isVisualDebuggerEnabled() && excalidrawAPI && (
          <DebugCanvas
            appState={excalidrawAPI.getAppState()}
            scale={window.devicePixelRatio}
            ref={debugCanvasRef}
          />
        )}
        <SpryDrivePicker
          isOpen={showDrivePicker}
          onOpen={(context) => {
            setShowDrivePicker(false);
            openBoardTarget(context);
          }}
          onCreateBoard={() => {
            setShowDrivePicker(false);
            void createBoardAndNavigate();
          }}
          onClose={() => setShowDrivePicker(false)}
        />
      </Excalidraw>
    </div>
  );
};

const ExcalidrawApp = () => {
  return (
    <TopErrorBoundary>
      <Provider store={appJotaiStore}>
        <ExcalidrawWrapper />
      </Provider>
    </TopErrorBoundary>
  );
};


export default ExcalidrawApp;
